import type { WorkspacePlanId } from '~/types/models';
import { resolveWorkspaceEntitlements } from '~/lib/subscriptionPlans';
import { normalizeStorageBytes, toStorageLimitBytes } from '~/lib/storageUsage';

export type WorkspaceFeatureKey =
    | 'analytics'
    | 'workflows'
    | 'projectTemplates'
    | 'recurringTasks'
    | 'portalBranding'
    | 'depositsAndPaymentPlans'
    | 'customDomain'
    | 'customEmailDomain'
    | 'fullPlatformBranding';

const FEATURE_REQUIREMENTS: Record<WorkspaceFeatureKey, { plan: WorkspacePlanId | 'growth_addon'; label: string }> = {
    analytics: { plan: 'growth', label: 'Growth' },
    workflows: { plan: 'growth', label: 'Growth' },
    projectTemplates: { plan: 'growth', label: 'Growth' },
    recurringTasks: { plan: 'growth', label: 'Growth' },
    portalBranding: { plan: 'growth', label: 'Growth' },
    depositsAndPaymentPlans: { plan: 'growth', label: 'Growth' },
    customDomain: { plan: 'growth_addon', label: 'Growth + Branding or Pro' },
    customEmailDomain: { plan: 'pro', label: 'Pro' },
    fullPlatformBranding: { plan: 'pro', label: 'Pro' },
};

const ACTIVE_PROJECT_STATUSES = new Set(['completed', 'archived']);

export function useWorkspaceSubscription() {
    const tenantStore = useTenantStore();
    const projectStore = useProjectStore();

    const entitlements = computed(() => resolveWorkspaceEntitlements(tenantStore.currentTenant));
    const subscription = computed(() => entitlements.value.plan);

    const activeProjectsCount = computed(() => (
        projectStore.projects.filter((project) => !ACTIVE_PROJECT_STATUSES.has(String(project.status || '').toLowerCase())).length
    ));

    const projectLimit = computed(() => entitlements.value.plan.features.projectLimit);
    const hasUnlimitedProjects = computed(() => projectLimit.value === 'unlimited');
    const projectLimitReached = computed(() => (
        typeof projectLimit.value === 'number'
        && activeProjectsCount.value >= projectLimit.value
    ));
    const remainingProjectSlots = computed(() => (
        typeof projectLimit.value === 'number'
            ? Math.max(projectLimit.value - activeProjectsCount.value, 0)
            : null
    ));

    const storageUsageBytes = computed(() => normalizeStorageBytes(tenantStore.currentTenant?.storageUsageBytes));
    const storageLimitBytes = computed(() => toStorageLimitBytes(entitlements.value.storageGb));
    const storageRemainingBytes = computed(() => Math.max(storageLimitBytes.value - storageUsageBytes.value, 0));
    const storageUsagePercent = computed(() => (
        storageLimitBytes.value > 0
            ? Math.min(100, Math.round((storageUsageBytes.value / storageLimitBytes.value) * 100))
            : 0
    ));
    const storageLimitReached = computed(() => storageUsageBytes.value >= storageLimitBytes.value);

    const canUseAnalytics = computed(() => entitlements.value.plan.features.analytics);
    const canUseWorkflows = computed(() => entitlements.value.plan.features.workflows);
    const canUseProjectTemplates = computed(() => entitlements.value.plan.features.projectTemplates);
    const canUseRecurringTasks = computed(() => entitlements.value.plan.features.recurringTasks);
    const canUsePortalBranding = computed(() => entitlements.value.plan.features.clientPortal !== 'basic');
    const canUseDepositsAndPaymentPlans = computed(() => entitlements.value.plan.features.depositsAndPaymentPlans);
    const canUseCustomDomains = computed(() => entitlements.value.customDomainEnabled);
    const canUseCustomEmailDomains = computed(() => entitlements.value.customEmailDomainEnabled);
    const canUseFullPlatformBranding = computed(() => entitlements.value.fullPlatformBrandingEnabled);

    function canAccessFeature(feature: WorkspaceFeatureKey): boolean {
        switch (feature) {
            case 'analytics':
                return canUseAnalytics.value;
            case 'workflows':
                return canUseWorkflows.value;
            case 'projectTemplates':
                return canUseProjectTemplates.value;
            case 'recurringTasks':
                return canUseRecurringTasks.value;
            case 'portalBranding':
                return canUsePortalBranding.value;
            case 'depositsAndPaymentPlans':
                return canUseDepositsAndPaymentPlans.value;
            case 'customDomain':
                return canUseCustomDomains.value;
            case 'customEmailDomain':
                return canUseCustomEmailDomains.value;
            case 'fullPlatformBranding':
                return canUseFullPlatformBranding.value;
            default:
                return false;
        }
    }

    function requiredLabelFor(feature: WorkspaceFeatureKey): string {
        return FEATURE_REQUIREMENTS[feature].label;
    }

    return {
        entitlements,
        subscription,
        activeProjectsCount,
        projectLimit,
        hasUnlimitedProjects,
        projectLimitReached,
        remainingProjectSlots,
        storageUsageBytes,
        storageLimitBytes,
        storageRemainingBytes,
        storageUsagePercent,
        storageLimitReached,
        canUseAnalytics,
        canUseWorkflows,
        canUseProjectTemplates,
        canUseRecurringTasks,
        canUsePortalBranding,
        canUseDepositsAndPaymentPlans,
        canUseCustomDomains,
        canUseCustomEmailDomains,
        canUseFullPlatformBranding,
        canAccessFeature,
        requiredLabelFor,
    };
}
